import React, { useEffect, useMemo } from 'react';
import {
    Form,
    Select,
    InputNumber,
    Switch,
    Button,
    Divider,
    Row,
    Col,
    Input,
    message,
    Alert,
} from 'antd';
import { useMutation, useQueryClient } from 'react-query';
import { upsertTransition } from '../../../config/services/patrolService';
import { parseApiError, bindFormErrors } from '../../../config/services/errorHandler';
import useColors from '../../../hooks/useColors';

const { Option } = Select;

/**
 * TransitionForm — Create or edit a checkpoint transition (patrol route edge).
 *
 * Props:
 *   propertyId      {number}
 *   checkpoints     {array}   active checkpoints [{id, name}]
 *   existingGroups  {string[]} existing alternativeGroup values for the "reuse" selector
 *   initialData     {object|null}
 *   onSuccess       {function}
 *   onCancel        {function}
 */
const TransitionForm = ({
    propertyId,
    checkpoints = [],
    existingGroups = [],
    initialData = null,
    onSuccess,
    onCancel,
}) => {
    const { colorPrimary } = useColors();
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const isEdit = !!initialData;

    useEffect(() => {
        if (initialData) {
            form.setFieldsValue({
                fromCheckpointId: initialData.fromCheckpoint?.id,
                toCheckpointId: initialData.toCheckpoint?.id,
                expectedMinutes: initialData.expectedMinutes,
                graceMinutes: initialData.graceMinutes ?? 0,
                alternativeGroup: initialData.alternativeGroup ?? null,
                isRequired: initialData.isRequired ?? true,
                isActive: initialData.isActive ?? true,
            });
        } else {
            form.resetFields();
            form.setFieldsValue({ isRequired: true, isActive: true, graceMinutes: 0 });
        }
    }, [initialData]);

    const saveMutation = useMutation(
        (data) => upsertTransition(propertyId, data),
        {
            onSuccess: (result) => {
                message.success('Transition saved.');
                queryClient.invalidateQueries(['checkpoint-transitions', propertyId]);
                onSuccess?.(result);
            },
            onError: (err) => {
                const parsed = parseApiError(err);
                if (parsed.fields) bindFormErrors(form, parsed.fields);
                else message.error(parsed.message);
            },
        }
    );

    const handleSubmit = (values) => {
        if (values.fromCheckpointId === values.toCheckpointId) {
            form.setFields([
                { name: 'toCheckpointId', errors: ['Source and destination must be different checkpoints.'] },
            ]);
            return;
        }
        saveMutation.mutate({
            fromCheckpointId: values.fromCheckpointId,
            toCheckpointId: values.toCheckpointId,
            expectedMinutes: values.expectedMinutes,
            graceMinutes: values.graceMinutes ?? 0,
            alternativeGroup: values.alternativeGroup || null,
            isRequired: values.isRequired ?? true,
            isActive: values.isActive ?? true,
        });
    };

    return (
        <Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="From Checkpoint"
                        name="fromCheckpointId"
                        rules={[{ required: true, message: 'Please select the source checkpoint.' }]}
                    >
                        <Select
                            placeholder="Select source"
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {checkpoints.map((c) => (
                                <Option key={c.id} value={c.id}>{c.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                    <Form.Item
                        label="To Checkpoint"
                        name="toCheckpointId"
                        rules={[{ required: true, message: 'Please select the destination checkpoint.' }]}
                    >
                        <Select
                            placeholder="Select destination"
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {checkpoints.map((c) => (
                                <Option key={c.id} value={c.id}>{c.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="Expected Travel (minutes)"
                        name="expectedMinutes"
                        rules={[
                            { required: true, message: 'Expected minutes required.' },
                            { type: 'integer', min: 1, max: 1440, message: '1–1,440 minutes.' },
                        ]}
                    >
                        <InputNumber style={{ width: '100%' }} min={1} max={1440} addonAfter="min" />
                    </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                    <Form.Item
                        label="Grace Period (minutes)"
                        name="graceMinutes"
                        rules={[
                            { type: 'integer', min: 0, max: 1440, message: '0–1,440 minutes.' },
                        ]}
                    >
                        <InputNumber style={{ width: '100%' }} min={0} max={1440} addonAfter="min" />
                    </Form.Item>
                </Col>
            </Row>

            {/* <Divider orientation="left" orientationMargin={0} plain style={{ fontSize: 12, color: '#6b7280' }}>
                Alternative Route Group
            </Divider>

            <Alert
                type="info"
                showIcon
                message='Transitions sharing the same non-empty group name satisfy each other — guards can scan any one of the alternatives.'
                style={{ marginBottom: 16, fontSize: 12 }}
            />

            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        label="Alternative Group (optional)"
                        name="alternativeGroup"
                        rules={[{ max: 80, message: 'Max 80 characters.' }]}
                    >
                        <Select
                            placeholder="Select existing or type new group key"
                            allowClear
                            showSearch
                            mode={undefined}
                            dropdownRender={(menu) => (
                                <>
                                    {menu}
                                    <Divider style={{ margin: '6px 0' }} />
                                    <div style={{ padding: '4px 8px', fontSize: 11, color: '#9ca3af' }}>
                                        Type a new group key above to create a new group
                                    </div>
                                </>
                            )}
                            options={existingGroups.map((g) => ({ label: g, value: g }))}
                            filterOption={(input, option) =>
                                option?.label?.toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                    <Form.Item label="Required" name="isRequired" valuePropName="checked">
                        <Switch checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                </Col>

                <Col xs={24} md={6}>
                    <Form.Item label="Active" name="isActive" valuePropName="checked">
                        <Switch checkedChildren="Yes" unCheckedChildren="No" />
                    </Form.Item>
                </Col>
            </Row> */}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                <Button onClick={onCancel} style={{ height: '38px', borderRadius: '6px', fontWeight: 500 }}>Cancel</Button>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={saveMutation.isLoading}
                    style={{
                        backgroundColor: colorPrimary,
                        border: 'none',
                        color: '#fff',
                        height: '38px',
                        borderRadius: '6px',
                        fontWeight: 500,
                        boxShadow: 'none',
                    }}
                >
                    {isEdit ? 'Save Changes' : 'Create Transition'}
                </Button>
            </div>
        </Form>
    );
};

export default TransitionForm;
